import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import './FCSpecs.css'



const SPECS = [
    {
        id: 'voltage',
        label: 'Input Voltage',
        value: '5V',
        unit: null,
        sub: 'USB / regulated supply',
        accent: '#5d179e',
        size: 'large',
    },
    {
        id: 'motors',
        label: 'Motor Outputs',
        value: '4',
        unit: 'PWM',
        sub: 'Standard ESC signal',
        accent: '#5d179e',
        size: 'large',
    },
    {
        id: 'imu',
        label: 'IMU',
        value: '3-Axis',
        unit: null,
        sub: 'Gyroscope + Accelerometer',
        accent: '#5d179e',
        size: 'medium',
    },
    {
        id: 'receiver',
        label: 'RC Receiver Input',
        value: 'PPM',
        unit: null,
        sub: 'Standard RC protocol',
        accent: '#5d179e',
        size: 'medium',
    },
    {
        id: 'pid',
        label: 'PID Tuning',
        value: 'None',
        unit: 'required',
        sub: 'Beginner-safe defaults',
        accent: '#5d179e',
        size: 'medium',
    },
    {
        id: 'rtf',
        label: 'Operation',
        value: 'RTF',
        unit: null,
        sub: 'Ready to fly',
        accent: '#5d179e',
        size: 'medium',
    },
]

const FLAGS = [
    { icon: '◈', label: 'Real-time flight controller' },
    { icon: '◈', label: 'Integrated IMU for stable flight' },
    { icon: '◈', label: 'Beginner-safe flight tuning' },
    { icon: '◈', label: 'Ideal for STEM education' },
]

function SpecCard({ spec, index }) {
    const ref = useRef(null)

    useEffect(() => {
        gsap.fromTo(ref.current,
            { opacity: 0, y: 36, scale: 0.97 },
            {
                opacity: 1, y: 0, scale: 1,
                duration: 0.75, ease: 'power3.out',
                delay: index * 0.07,
                scrollTrigger: {
                    trigger: ref.current,
                    start: 'top 88%',
                    toggleActions: 'play none none none',
                },
            }
        )
    }, [index])

    return (
        <div ref={ref} className={`fcs-card fcs-card--${spec.size}`} style={{ opacity: 0 }}>
            <span className="fcs-card-label">{spec.label}</span>
            <div className="fcs-card-value-row">
                <span className="fcs-card-value">{spec.value}</span>
                {spec.unit && <span className="fcs-card-unit">{spec.unit}</span>}
            </div>
            <span className="fcs-card-sub">{spec.sub}</span>
            <div className="fcs-card-bar" />
        </div>
    )
}

export default function FCSpecs() {
    const wrapRef = useRef(null)
    const eyebrowRef = useRef(null)
    const headingRef = useRef(null)
    const flagsRef = useRef(null)

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.fromTo(eyebrowRef.current,
                { opacity: 0, y: 20 },
                {
                    opacity: 1, y: 0, duration: 0.7, ease: 'expo.out',
                    scrollTrigger: { trigger: eyebrowRef.current, start: 'top 88%', toggleActions: 'play none none none' },
                }
            )
            gsap.fromTo(headingRef.current,
                { opacity: 0, y: 50 },
                {
                    opacity: 1, y: 0, duration: 1.0, ease: 'expo.out',
                    scrollTrigger: { trigger: headingRef.current, start: 'top 85%', toggleActions: 'play none none none' },
                }
            )
            gsap.fromTo(flagsRef.current,
                { opacity: 0, y: 30 },
                {
                    opacity: 1, y: 0, duration: 0.85, ease: 'expo.out',
                    scrollTrigger: { trigger: flagsRef.current, start: 'top 88%', toggleActions: 'play none none none' },
                }
            )
        }, wrapRef)
        return () => ctx.revert()
    }, [])

    return (
        <section ref={wrapRef} className="fcs-wrap">
            <div className="fcs-bg-grid" />

            <div className="fcs-inner">
                {/* ── Header ── */}
                <div className="fcs-header">
                    <div ref={eyebrowRef} className="fcs-eyebrow" style={{ opacity: 0 }}>
                        <span className="fcs-dot" />TECH SPECS
                    </div>
                    <h2 ref={headingRef} className="fcs-h2" style={{ opacity: 0 }}>
                        Core System <em>Architecture.</em>
                    </h2>
                </div>

                {/* ── Spec cards grid ── */}
                <div className="fcs-grid">
                    {SPECS.map((spec, i) => (
                        <SpecCard key={spec.id} spec={spec} index={i} />
                    ))}
                </div>

                {/* ── Feature flags row ── */}
                <div ref={flagsRef} className="fcs-flags" style={{ opacity: 0 }}>
                    {FLAGS.map((f, i) => (
                        <div key={i} className="fcs-flag">
                            <span className="fcs-flag-icon">{f.icon}</span>
                            <span className="fcs-flag-label">{f.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )
}